import { generateCompareUrl } from '@oneaudi/feature-app-utils/compareUtils';
import { Divider, TextButton, TextLink } from '@oneaudi/unified-web-components';
import type { FC } from 'react';
import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import { CompareContext } from '../../context/CompareContext';
import { useContent } from '../../context/ContentContext';
import { createCompareCheckboxId } from '../../context/compareContextUtils';
import { useI18n } from '../../i18n';
import { FlyoutItem } from './FlyoutItems';
import { useCompareTrackingManager } from '../../hooks/useCompareTrackingManager';

const Wrapper = styled.div`
  position: sticky;
  bottom: -1px;
  background: ${({ theme }) => theme.colors.backgroundComparebar};
  z-index: 30; // this is because of the fancy Action flyout <3
  padding: var(--spacing-relative-md) var(--page-margin);
  display: grid;
  grid-template-rows: auto 1fr auto auto auto;
  transition: grid-template-rows 320ms;

  &.closed {
    grid-template-rows: auto 0fr 0fr 0fr 0fr;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    grid-template-columns: 1fr min-content min-content;
  }
`;

const ToggleButton = styled(TextButton)`
  margin-inline-end: auto;
  padding-inline-start: 0;
`;

const StyledDivider = styled(Divider)`
  margin-block: ${({ theme }) => theme.spacing.fixed300};
  transition-timing-function: ease-in;
  transition-duration: 320ms;
  transition-property: transform, opacity, visibility, margin;
  opacity: 1;
  visibility: visible;

  .closed & {
    opacity: 0;
    visibility: hidden;
    margin: 0;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    display: none;
  }
`;

const CarlineGroup = styled.div<{ numberOfCarlines: 2 | 3 }>`
  flex-basis: 100%;
  display: grid;
  margin-block-start: var(--spacing-relative-md);
  row-gap: ${({ theme }) => theme.spacing.fixed350};
  column-gap: ${({ theme }) => theme.spacing.fixed300};

  overflow: hidden;
  transition-timing-function: ease-in;
  transition-duration: 320ms;
  transition-property: transform, opacity, visibility, margin;
  opacity: 1;
  visibility: visible;

  .closed & {
    opacity: 0;
    visibility: hidden;
    margin: 0;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    order: 4;
    grid-column-end: span 3;
    grid-template-columns: repeat(${({ numberOfCarlines }) => numberOfCarlines}, 1fr);
  }
`;

const RemoveAllButton = styled(TextLink)`
  justify-self: center;
  margin-block-end: ${({ theme }) => theme.spacing.fixed350};
  white-space: nowrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.md - 1}px) {
    .closed & {
      display: none;
    }
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    margin-block: 0;
    margin-inline-end: ${({ theme }) => theme.spacing.fixed350};
  }
`;

const CompareLink = styled(TextButton)`
  @media (max-width: ${({ theme }) => theme.breakpoints.md - 1}px) {
    .closed & {
      display: none;
    }
  }
`;

export const CompareFlyout: FC = () => {
  const { state, dispatch } = useContext(CompareContext);
  const { compareLink } = useContent();
  const [open, setOpen] = useState(state.amount !== 2);
  const toggleButtonText = useI18n('compare.barToggleButtonText', {
    selected: state.carlines.length,
    limit: state.amount,
  });
  const removeAllButtonText = useI18n('compare.removeAllButtonText');
  const compareLinkText = useI18n('compare.compareLinkText');
  const {
    removeAll: trackRemoveAll,
    toggleStickyBar: trackToggle,
    compare: trackClick,
  } = useCompareTrackingManager();

  const focusLastCheckbox = () => {
    const lastCarline = state.carlines[state.carlines.length - 1];
    const chkbxId = createCompareCheckboxId(lastCarline.id);
    document.getElementById(chkbxId)?.focus();
  };
  const compareURL = generateCompareUrl(
    compareLink ?? '',
    state.carlines.map(({ id }) => id),
  );

  return (
    <Wrapper
      className={!open ? 'closed' : undefined}
      onKeyUp={({ key }) => {
        if (key === 'Escape') {
          focusLastCheckbox();
        }
      }}
    >
      <ToggleButton
        id="compare-toggle"
        variant="tertiary"
        icon={open ? 'arrow-up' : 'arrow-down'}
        iconPosition="leading"
        onClick={() => {
          setOpen((current) => {
            const shouldOpen = !current;
            trackToggle({ open: shouldOpen });
            return shouldOpen;
          });
        }}
        aria-expanded={open}
        aria-controls="compare-carlines"
        size="sm"
      >
        {toggleButtonText}
      </ToggleButton>
      <CarlineGroup
        id="compare-carlines"
        aria-hidden={!open}
        aria-labelledby="compare-toggle"
        role="region"
        numberOfCarlines={state.amount}
      >
        <FlyoutItem carline={state.carlines[0]} />
        <FlyoutItem carline={state.carlines[1]} />
        {state.amount === 3 && <FlyoutItem carline={state.carlines[2]} />}
      </CarlineGroup>
      <StyledDivider />
      <RemoveAllButton
        variant="standalone"
        size="sm"
        onClick={() => {
          focusLastCheckbox();
          trackRemoveAll({ label: removeAllButtonText });
          dispatch({ type: 'clear' });
        }}
      >
        {removeAllButtonText}
      </RemoveAllButton>
      <CompareLink
        variant="primary"
        size="sm"
        href={compareURL}
        onClick={(e) => {
          trackClick({ label: compareLinkText, targetURL: e.currentTarget.href });
        }}
      >
        {compareLinkText}
      </CompareLink>
    </Wrapper>
  );
};
